import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Truck, Calculator, Package, ArrowRight, CheckCircle2, Sparkles, Leaf } from 'lucide-react';

export const Home = () => {
  const services = [
    {
      icon: Truck,
      title: 'Ritiro Cavi Elettrici',
      description: 'Ritiriamo cavi elettrici in Canavese, Torino e provincia. Servizio dedicato per elettricisti, installatori e imprese.',
    },
    {
      icon: Calculator,
      title: 'Quotazione Trasparente',
      description: 'Quotazione chiara per acquistare i vostri cavi, basata su resa reale e non stime generiche.',
    },
    {
      icon: Package,
      title: 'Recupero Rame e Polimeri',
      description: 'Recupero rame con purezza certificata del 99% e riciclo polimeri di alta qualità per l\'economia circolare.',
    },
  ];

  const homeBenefits = [
    'Quotazioni precise basate su resa reale',
    'Ritiri su misura per elettricisti, installatori e imprese',
    'Documentazione completa e report ambientali inclusi',
    'Un referente dedicato che conosce il materiale e il settore',
  ];

  return (
    <div className="pt-20">
      {/* Hero Section Premium */}
      <section className="relative min-h-[600px] md:min-h-[700px] flex items-center overflow-hidden bg-gradient-to-br from-[#fef8f5] via-white to-[#f0f7f5]">
        {/* Animated Background Blobs */}
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-20 right-20 w-64 md:w-96 h-64 md:h-96 bg-[var(--copper-primary)] rounded-full blur-3xl animate-float"></div>
          <div className="absolute bottom-20 left-20 w-64 md:w-96 h-64 md:h-96 bg-[var(--verdigris)] rounded-full blur-3xl animate-float" style={{ animationDelay: '1.5s' }}></div>
        </div>
        
        {/* Decorative Pattern */}
        <div className="absolute inset-0 pattern-dots opacity-30"></div>
        
        <div className="container relative z-10 py-8 md:py-0">
          <div className="max-w-4xl">
            {/* Badge Premium */}
            <div className="inline-flex items-center gap-2 px-4 py-2 mb-8 bg-white/90 backdrop-blur-sm rounded-full border border-[var(--copper-primary)]/30 shadow-lg">
              <Sparkles className="h-4 w-4 text-[var(--copper-primary)]" />
              <span className="text-sm font-semibold text-[var(--copper-primary)]">Rame al 99% di purezza certificata</span>
            </div>
            
            {/* Main Heading */}
            <h1 className="text-5xl sm:text-6xl md:text-6xl lg:text-7xl font-bold mb-6 md:mb-8 leading-[1.1] animate-fade-in">
              <span className="block md:inline">Dal Cavo </span>
              <span className="block md:inline">alla Risorsa</span>
              <span className="block mt-2 md:mt-3 text-4xl sm:text-5xl md:text-5xl lg:text-6xl bg-gradient-to-r from-[var(--copper-primary)] via-[var(--verdigris)] to-[var(--verdigris-dark)] bg-clip-text text-transparent leading-tight">
                Ritiro e Riciclo Cavi Elettrici
              </span>
            </h1>
            
            {/* Description */}
            <p className="text-lg sm:text-xl md:text-xl lg:text-2xl mb-8 md:mb-10 text-[var(--text-light)] leading-relaxed max-w-3xl animate-fade-in" style={{ animationDelay: '0.2s' }}>
              Siamo specializzati nel riciclo dei cavi elettrici, in Canavese, Torino e provincia. Trasformiamo i tuoi cavi di scarto in risorse preziose: rame al 99% di purezza certificata e polimeri di qualità.
            </p>
            
            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-3 md:gap-4 animate-fade-in" style={{ animationDelay: '0.4s' }}>
              <Link to="/servizi" className="w-full sm:w-auto">
                <Button
                  size="lg"
                  className="w-full sm:w-auto bg-[var(--copper-primary)] hover:bg-[var(--copper-dark)] text-white px-6 md:px-10 py-6 md:py-7 text-base md:text-lg shadow-lg hover:shadow-copper transition-all duration-300 hover:-translate-y-1 font-semibold"
                >
                  Scopri i Servizi
                  <ArrowRight className="ml-2 h-4 w-4 md:h-5 md:w-5" />
                </Button>
              </Link>
              <Link to="/contatti" className="w-full sm:w-auto">
                <Button
                  size="lg"
                  variant="outline"
                  className="w-full sm:w-auto border-2 border-[var(--copper-primary)] text-[var(--copper-primary)] hover:bg-[var(--copper-primary)] hover:text-white px-6 md:px-10 py-6 md:py-7 text-base md:text-lg transition-all duration-300 hover:-translate-y-1 font-semibold"
                >
                  Richiedi Quotazione
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section con Numeri Evidenti */}
      <section className="py-20 bg-gradient-to-br from-[#2a2a2a] via-[var(--text-dark)] to-[#1a1a1a] text-white relative overflow-hidden">
        {/* Pattern Grid Background */}
        <div className="absolute inset-0 pattern-grid opacity-10"></div>
        
        <div className="container relative z-10">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12 text-center">
            {/* Stat 1: Anno */}
            <div className="group relative">
              <div className="absolute inset-0 bg-[var(--verdigris)] opacity-0 group-hover:opacity-10 blur-3xl transition-opacity duration-500 rounded-full"></div>
              <div className="relative">
                <div className="text-6xl md:text-7xl font-extrabold mb-4 bg-gradient-to-r from-[var(--verdigris)] to-[var(--verdigris-light)] bg-clip-text text-transparent">
                  2025
                </div>
                <div className="text-gray-300 text-lg font-medium">Anno di Fondazione</div>
              </div>
            </div>

            {/* Stat 2: Purezza */}
            <div className="group relative">
              <div className="absolute inset-0 bg-[var(--copper-primary)] opacity-0 group-hover:opacity-10 blur-3xl transition-opacity duration-500 rounded-full"></div>
              <div className="relative">
                <div className="text-6xl md:text-7xl font-extrabold mb-4 bg-gradient-to-r from-[var(--copper-primary)] to-[var(--copper-light)] bg-clip-text text-transparent">
                  99%
                </div>
                <div className="text-gray-300 text-lg font-medium mb-3">Purezza Rame</div>
                <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-white/10 backdrop-blur-sm rounded-full text-sm font-semibold">
                  <CheckCircle2 className="h-4 w-4" />
                  Certificato
                </div>
              </div>
            </div>

            {/* Stat 3: Autorizzazione */}
            <div className="group relative">
              <div className="absolute inset-0 bg-[var(--verdigris)] opacity-0 group-hover:opacity-10 blur-3xl transition-opacity duration-500 rounded-full"></div>
              <div className="relative">
                <div className="text-6xl md:text-7xl font-extrabold mb-4 bg-gradient-to-r from-[var(--verdigris)] to-[var(--verdigris-light)] bg-clip-text text-transparent">
                  R13
                </div>
                <div className="text-gray-300 text-lg font-medium">Autorizzazione</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section Premium */}
      <section className="py-20 md:py-24 bg-gradient-to-b from-white to-[var(--warm-gray)]">
        <div className="container">
          {/* Section Header */}
          <div className="text-center mb-12 md:mb-16">
            <div className="inline-flex items-center gap-2 px-4 py-2 mb-6 bg-[var(--verdigris)]/10 rounded-full">
              <Leaf className="h-4 w-4 text-[var(--verdigris)]" />
              <span className="text-sm font-semibold text-[var(--verdigris)]">Economia Circolare</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold mb-6">I Nostri Servizi</h2>
            <p className="text-lg md:text-xl text-[var(--text-light)] max-w-3xl mx-auto leading-relaxed">
              Offriamo soluzioni complete per il recupero e la valorizzazione di materiali da cavi elettrici
            </p>
          </div>

          {/* Services Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8 mb-12">
            {services.map((service, index) => {
              const Icon = service.icon;
              return (
                <Card
                  key={index}
                  className="group border-2 border-gray-200 hover:border-[var(--copper-primary)] transition-all duration-500 hover:shadow-xl hover:-translate-y-2 bg-white overflow-hidden"
                >
                  <CardContent className="p-6 md:p-8 relative">
                    {/* Decorative Background Circle */}
                    <div className="absolute top-0 right-0 w-32 md:w-40 h-32 md:h-40 bg-[var(--copper-primary)]/5 rounded-full -mr-16 md:-mr-20 -mt-16 md:-mt-20 group-hover:scale-150 transition-transform duration-700"></div>
                    
                    <div className="relative">
                      {/* Icon with Gradient Background */}
                      <div className="w-16 h-16 md:w-20 md:h-20 bg-gradient-to-br from-[var(--copper-primary)] to-[var(--copper-dark)] rounded-xl md:rounded-2xl flex items-center justify-center mb-4 md:mb-6 shadow-lg group-hover:scale-110 group-hover:rotate-3 transition-all duration-300">
                        <Icon className="h-8 w-8 md:h-10 md:w-10 text-white" />
                      </div>
                      
                      {/* Content */}
                      <h3 className="text-xl md:text-2xl font-bold mb-3 md:mb-4 group-hover:text-[var(--copper-primary)] transition-colors duration-300">
                        {service.title}
                      </h3>
                      <p className="text-[var(--text-light)] leading-relaxed">
                        {service.description}
                      </p>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* CTA Button */}
          <div className="text-center">
            <Link to="/servizi">
              <Button
                variant="outline"
                size="lg"
                className="border-2 border-[var(--verdigris)] text-[var(--verdigris)] hover:bg-[var(--verdigris)] hover:text-white shadow-md hover:shadow-lg transition-all duration-300 px-8 py-6 text-lg font-semibold"
              >
                Vedi Tutti i Servizi
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Benefits Section - Perché Scegliere FIRM */}
      <section className="py-20 md:py-24 bg-gradient-to-br from-[var(--verdigris)] to-[var(--verdigris-dark)] text-white relative overflow-hidden">
        {/* Industrial Pattern Overlay */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `repeating-linear-gradient(
              45deg,
              transparent,
              transparent 35px,
              rgba(255,255,255,0.1) 35px,
              rgba(255,255,255,0.1) 70px
            )`
          }}></div>
        </div>
        
        {/* Decorative blobs */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 left-10 w-64 md:w-96 h-64 md:h-96 bg-white rounded-full blur-3xl"></div>
          <div className="absolute bottom-10 right-10 w-64 md:w-96 h-64 md:h-96 bg-white rounded-full blur-3xl"></div>
        </div>
        
        <div className="container relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-8 leading-tight">Perché Scegliere FIRM</h2>
            
            <p className="text-xl md:text-2xl mb-6 text-white/90 leading-relaxed max-w-3xl mx-auto">
              Siamo <strong>specializzati esclusivamente in cavi elettrici</strong>.
            </p>
            
            <p className="text-lg mb-10 text-white/80">
              Questo ci permette di garantire:
            </p>
            
            {/* Benefits Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-10 max-w-3xl mx-auto">
              {homeBenefits.map((benefit, index) => (
                <div key={index} className="flex items-center gap-4 p-5 bg-white/10 backdrop-blur-sm rounded-2xl border border-white/20 group hover:bg-white/15 transition-all duration-300">
                  <div className="flex-shrink-0 w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                    <CheckCircle2 className="h-6 w-6 text-white" />
                  </div>
                  <span className="text-lg text-white font-medium text-left">{benefit}</span>
                </div>
              ))}
            </div>
            
            <p className="text-lg mb-12 text-white/90 leading-relaxed max-w-3xl mx-auto">
              Il partner ideale per elettricisti, installatori elettrici e imprese edili che cercano <strong>trasparenza e affidabilità</strong> nello smaltimento cavi elettrici.
            </p>
            
            <Link to="/chi-siamo">
              <Button
                size="lg"
                className="bg-white text-[var(--verdigris)] hover:bg-gray-100 px-10 py-7 text-lg font-semibold shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
              >
                Scopri di Più
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Final Section */}
      <section className="py-20 md:py-24">
        <div className="container">
          <div className="bg-gradient-to-r from-[var(--copper-primary)] to-[var(--copper-dark)] rounded-3xl p-10 md:p-16 text-center text-white shadow-2xl relative overflow-hidden">
            {/* Decorative Pattern */}
            <div className="absolute inset-0 pattern-dots opacity-10"></div>
            
            <div className="relative z-10">
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6 leading-tight">Hai Cavi Elettrici da Smaltire/Riciclare?</h2>
              <p className="text-lg md:text-xl mb-10 max-w-3xl mx-auto text-white/90 leading-relaxed">
                Dove smaltire cavi elettrici vicino Torino? Contattaci per una quotazione gratuita. Ritiriamo in tutto il Canavese e provincia di Torino.
              </p>
              <Link to="/contatti">
                <Button
                  size="lg"
                  className="bg-white text-[var(--copper-primary)] hover:bg-gray-100 px-10 py-7 text-lg shadow-xl font-semibold"
                >
                  Richiedi Quotazione Gratuita
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};
